import { useLang } from '@/hooks/useLang';

const BASE = 'https://www.yamamoto-ss.co.jp/sp_recycle';

export default function Footer() {
  const { t } = useLang();

  const productLinks = [
    { label: t('EPS Compactors / Densifiers', '発泡スチロール減容機'), href: `${BASE}/products/melter/` },
    { label: 'RE-E201', href: `${BASE}/products/melter/re-e201.html`, sub: true },
    { label: 'RE-E502/502X', href: `${BASE}/products/melter/re-e502.html`, sub: true },
    { label: 'RE-E1000', href: `${BASE}/products/melter/re-e1000.html`, sub: true },
    { label: 'RE-E1000A/1500/2000/3000', href: `${BASE}/products/melter/re-e1000a-3000.html`, sub: true },
    { label: t('PET Bottle Recycling Equipment', 'ペットボトルリサイクル機器'), href: `${BASE}/products/pet/` },
    { label: 'PLS-2000 / PLS-5000', href: `${BASE}/products/pet/pls-5000.html`, sub: true },
    { label: 'PBD-5000 / MBS-3000', href: `${BASE}/products/pet/mbs-3000.html`, sub: true },
  ];

  const moreLinks = [
    { label: t('Bag Breakers', '破袋機'), href: `${BASE}/products/bag/` },
    { label: t('Vertical Conveyors', '垂直搬送機'), href: `${BASE}/products/transport/` },
    { label: t('Granule Compactors', '造粒減容機'), href: `${BASE}/products/grush/` },
    { label: t('"Purasiru" Plastic ID', 'ぷらしる'), href: `${BASE}/products/placil/` },
  ];

  const companyLinks = [
    { label: t('Case Studies', '導入事例'), href: `${BASE}/case/` },
    { label: t('Our Philosophy', '開発への思い'), href: `${BASE}/concept/` },
    { label: t('Introduction', 'はじめに'), href: `${BASE}/about/` },
    { label: t('Mission', '事業ミッション'), href: `${BASE}/about/mission.html` },
    { label: t('Our History', '環境機器事業部の歩み'), href: `${BASE}/about/history.html` },
    { label: t('Download Catalog', 'カタログダウンロード'), href: `${BASE}/products/catalog.html` },
    { label: t('Contact Us', 'お問い合わせ'), href: `${BASE}/contact/` },
  ];

  return (
    <footer className="bg-[#0d0d0d] text-white">
      {/* CTA strip */}
      <div className="border-b border-white/10">
        <div className="max-w-screen-xl mx-auto px-8 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-xs text-[#c0392b] font-bold uppercase tracking-[0.2em] mb-2">
              {t('Ready to get started?', 'お問い合わせはこちら')}
            </p>
            <h3 className="text-2xl font-black text-white">
              {t('Talk to our EPS recycling experts.', 'EPSリサイクルの専門家にご相談ください。')}
            </h3>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <a
              href={`${BASE}/products/catalog.html`}
              className="flex items-center gap-2 border border-white/30 text-white text-sm font-bold px-6 py-3 hover:border-white transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line"></i>
              {t('Download Catalog', 'カタログ')}
            </a>
            <a
              href={`${BASE}/contact/`}
              className="flex items-center gap-2 bg-[#c0392b] text-white text-sm font-bold px-6 py-3 hover:bg-[#a93226] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              {t('Contact Us', 'お問い合わせ')}
            </a>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-screen-xl mx-auto px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-[#c0392b]"></div>
              <span className="text-lg font-black tracking-[0.15em] uppercase">Yamamoto</span>
            </div>
            <p className="text-white/40 text-xs leading-relaxed mb-5">
              {t(
                'Environmental Equipment Division — EPS Recycling Solutions for North America.',
                '環境機器事業部 — 北米向けEPSリサイクルソリューション'
              )}
            </p>
            <a href="https://www.shigenpla.com/" target="_blank" rel="noopener noreferrer" className="cursor-pointer block">
              <img
                src="https://www.yamamoto-ss.co.jp/sp_recycle/_resources/img_top/bn_shigenpla.png"
                alt="Shigen Plastic Association"
                className="h-8 object-contain opacity-60 hover:opacity-100 transition-opacity"
              />
            </a>
          </div>

          {/* Products */}
          <div>
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] mb-5">
              {t('Products', '製品一覧')}
            </p>
            <ul className="space-y-2">
              {productLinks.map(l => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className={`text-xs transition-colors hover:text-[#c0392b] ${l.sub ? 'text-white/30 pl-3' : 'text-white/60 font-medium'}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* More products */}
          <div>
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] mb-5">
              {t('More Equipment', 'その他の機器')}
            </p>
            <ul className="space-y-2">
              {moreLinks.map(l => (
                <li key={l.label}>
                  <a href={l.href} className="text-xs text-white/60 hover:text-[#c0392b] transition-colors font-medium">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] mb-5">
              {t('Company', '会社情報')}
            </p>
            <ul className="space-y-2">
              {companyLinks.map(l => (
                <li key={l.label}>
                  <a href={l.href} className="text-xs text-white/60 hover:text-[#c0392b] transition-colors">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-screen-xl mx-auto px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} Yamamoto Co., Ltd. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a href="https://www.yamamoto-ss.co.jp/privacy/" target="_blank" rel="noopener noreferrer" className="text-xs text-white/30 hover:text-white/60 transition-colors">
              {t('Privacy Policy', '個人情報保護方針')}
            </a>
            <a href="https://www.yamamoto-ss.co.jp/" target="_blank" rel="noopener noreferrer" className="text-xs text-white/30 hover:text-white/60 transition-colors">
              {t('Yamamoto Co., Ltd.', '山本製作所')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
